/**
 * Game-layer withdrawal form — denominated in Gold, converted to EUR for the
 * core backend. The core `mountWithdrawForm` works in EUR cents and cannot be
 * edited from a game repo, so this reimplements the UI with Gold as the primary
 * unit and an EUR preview line.
 *
 * Rate: GOLD_PER_EUR = 100 (flat, matches backend/src/game/currency-shop/currency-shop.formulas.ts).
 */
import { esc } from '../../core/payments/esc';
import { feeCents, formatMoney } from '../../core/payments/methods';
import {
  normalise, fieldError,
} from '../../core/payments/billing-address';
import {
  requestWithdrawal, toPaymentError,
} from '../../core/payments/payments-api';
import {
  normaliseCardNumber, isValidCardNumber, formatCardInput,
  normaliseIban, isValidIban, formatIbanInput,
  normaliseBic, isValidBic, formatBicInput,
} from '../../core/payments/payout-validation';
import { withdrawalErrorMessage } from '../../core/payments/widgets/withdraw-form';
import type {
  CreateWithdrawalResponse, PayoutMethod, WithdrawalLimits,
} from '../../core/payments/types';
import {
  ALERT_ERR_CLS, ALERT_OK_CLS, BTN_CLS, FIELD_ERR_CLS, INPUT_CLS, LABEL_CLS,
} from '../../core/payments/widgets/classes';

const GOLD_PER_EUR = 100;

export interface GoldWithdrawFormOptions {
  methods: PayoutMethod[];
  limits: WithdrawalLimits;
  goldBalance: number;
  firstName?: string | null;
  lastName?: string | null;
  onSubmitted?: (res: CreateWithdrawalResponse) => void;
}

export interface GoldWithdrawFormHandle {
  destroy(): void;
}

function goldToEurCents(gold: number): number {
  return Math.round((gold / GOLD_PER_EUR) * 100);
}

function eurCentsToGold(cents: number): number {
  return Math.round((cents * GOLD_PER_EUR) / 100);
}

function fmtGold(g: number): string {
  return `${g.toLocaleString()} Gold`;
}

export function mountGoldWithdrawForm(el: HTMLElement, opts: GoldWithdrawFormOptions): GoldWithdrawFormHandle {
  const { limits } = opts;
  const methods = opts.methods.filter((m) => !limits.methods || limits.methods.includes(m.id));
  let method: PayoutMethod | null = methods[0] ?? null;
  let destroyed = false;

  const nameField = (name: string, label: string, value: string | null | undefined, ac: string) => `
    <label class="block">
      <span class="${LABEL_CLS} mb-1.5">${esc(label)}</span>
      <input type="text" name="${name}" value="${esc(value ?? '')}" autocomplete="${ac}" class="${INPUT_CLS}" />
      <span data-error="${name}" class="${FIELD_ERR_CLS}" hidden></span>
    </label>`;

  if (!methods.length) {
    el.innerHTML = `<p class="text-sm text-text-dim">Withdrawals are not available right now.</p>`;
    return { destroy() { el.innerHTML = ''; } };
  }

  const maxGold = eurCentsToGold(Math.min(limits.maxCents, limits.availableCents));
  const minGold = eurCentsToGold(limits.minCents);

  el.innerHTML = `
    <form class="w-full flex flex-col gap-3.5" novalidate>
      <p class="text-sm text-text-dim">
        Available: <strong class="text-gold">${esc(fmtGold(opts.goldBalance))}</strong>
        <span class="text-text-dim/60 ml-1">(${esc(formatMoney(goldToEurCents(opts.goldBalance), 'EUR'))})</span>
      </p>
      <p class="text-xs text-text-dim">Withdrawals are processed in EUR at 100 Gold = €1.</p>

      <div class="flex gap-2" role="radiogroup" aria-label="Payout method">
        ${methods.map((m) => `
          <button type="button" data-method="${esc(m.id)}" role="radio"
            class="flex-1 flex items-center justify-center gap-2 min-h-[44px] px-3 py-2 bg-surface border rounded touch-manipulation transition-colors duration-[120ms]">
            <span>${esc(m.label)}</span>
          </button>`).join('')}
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        ${nameField('firstName', 'First name', opts.firstName, 'given-name')}
        ${nameField('lastName', 'Last name', opts.lastName, 'family-name')}
      </div>

      <label class="block">
        <span data-role="receiver-label" class="${LABEL_CLS} mb-1.5"></span>
        <input type="text" name="receiver" inputmode="text" autocomplete="off" autocapitalize="characters" spellcheck="false" class="${INPUT_CLS}" />
        <span data-error="receiver" class="${FIELD_ERR_CLS}" hidden></span>
      </label>
      <label data-role="bic-field" class="block" hidden>
        <span class="${LABEL_CLS} mb-1.5">BIC / SWIFT</span>
        <input type="text" name="bankNumber" inputmode="text" autocomplete="off" autocapitalize="characters" spellcheck="false"
          maxlength="11" placeholder="BPKOPLPW" class="${INPUT_CLS}" />
        <span data-error="bankNumber" class="${FIELD_ERR_CLS}" hidden></span>
      </label>

      <label class="block">
        <span class="${LABEL_CLS} mb-1.5">Amount (Gold)</span>
        <input type="text" name="amount" inputmode="numeric" autocomplete="off" placeholder="0" class="${INPUT_CLS}" />
        <span data-error="amount" class="${FIELD_ERR_CLS}" hidden></span>
        <span class="block mt-1 text-xs text-text-dim">
          Min ${esc(fmtGold(minGold))} · Max ${esc(fmtGold(maxGold))}
        </span>
      </label>
      <p data-role="preview" class="text-sm text-text-dim" aria-live="polite"></p>
      <div data-role="error" class="${ALERT_ERR_CLS}" role="alert" hidden></div>
      <div data-role="ok" class="${ALERT_OK_CLS}" role="status" hidden></div>
      <button type="submit" class="${BTN_CLS}" ${limits.hasOpenRequest ? 'disabled' : ''}>Request withdrawal</button>
      ${limits.hasOpenRequest ? `<p class="text-[13px] text-text-dim">You already have a withdrawal in progress.</p>` : ''}
    </form>`;

  const form = el.querySelector('form') as HTMLFormElement;
  const inp = (n: string) => form.elements.namedItem(n) as HTMLInputElement;
  const q = (r: string) => el.querySelector(`[data-role="${r}"]`) as HTMLElement;
  const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;

  const setErr = (field: string, msg: string | null) => {
    const s = el.querySelector(`[data-error="${field}"]`) as HTMLElement;
    s.textContent = msg ?? '';
    s.hidden = !msg;
  };
  const clearAll = () => {
    ['firstName', 'lastName', 'receiver', 'bankNumber', 'amount'].forEach((f) => setErr(f, null));
    q('error').hidden = true;
    q('ok').hidden = true;
  };

  const renderMethod = () => {
    el.querySelectorAll<HTMLElement>('[data-method]').forEach((b) => {
      const on = b.dataset.method === method?.id;
      b.setAttribute('aria-checked', String(on));
      b.classList.toggle('border-gold', on);
      b.classList.toggle('shadow-glow', on);
      b.classList.toggle('border-border', !on);
    });
    const isCard = method?.receiver === 'cardNumber';
    q('receiver-label').textContent = isCard ? 'Card number' : 'IBAN';
    inp('receiver').inputMode = isCard ? 'numeric' : 'text';
    inp('receiver').value = '';
    setErr('receiver', null);
    const bicField = el.querySelector('[data-role="bic-field"]') as HTMLElement;
    if (bicField) { bicField.hidden = isCard; inp('bankNumber').value = ''; setErr('bankNumber', null); }
  };

  function parseGold(): number | null {
    const t = inp('amount').value.trim().replace(/,/g, '');
    if (!/^\d+$/.test(t)) return null;
    const g = Number(t);
    return g > 0 ? g : null;
  }

  const renderPreview = () => {
    const gold = parseGold();
    const p = q('preview');
    if (gold === null) { p.textContent = ''; return; }
    const cents = goldToEurCents(gold);
    const fee = feeCents(cents, limits.feeBps);
    const net = cents - fee;
    p.textContent = `${fmtGold(gold)} = ${formatMoney(cents, 'EUR')} · Fee ${formatMoney(fee, 'EUR')} · You receive ${formatMoney(net, 'EUR')}`;
  };

  const receiverError = (): string | null => {
    const v = inp('receiver').value;
    if (method?.receiver === 'cardNumber') {
      return isValidCardNumber(normaliseCardNumber(v)) ? null : 'Enter a valid card number';
    }
    return isValidIban(normaliseIban(v)) ? null : 'Enter a valid IBAN';
  };

  const onClick = (e: Event) => {
    const b = (e.target as HTMLElement).closest('[data-method]') as HTMLElement | null;
    if (!b) return;
    const m = methods.find((x) => x.id === b.dataset.method);
    if (m && m !== method) { method = m; renderMethod(); }
  };
  const onReceiverInput = () => {
    const i = inp('receiver');
    i.value = method?.receiver === 'cardNumber' ? formatCardInput(i.value) : formatIbanInput(i.value);
    setErr('receiver', null);
  };
  const onAmountInput = () => { setErr('amount', null); renderPreview(); };

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    clearAll();
    if (!method) return;

    const first = normalise(inp('firstName').value);
    const last = normalise(inp('lastName').value);
    let bad = false;
    for (const [f, v] of [['firstName', first], ['lastName', last]] as const) {
      const err = v ? fieldError(f, v) : 'Required';
      if (err) { setErr(f, err); bad = true; }
    }
    const rErr = receiverError();
    if (rErr) { setErr('receiver', rErr); bad = true; }
    if (method?.receiver !== 'cardNumber') {
      const bic = normaliseBic(inp('bankNumber').value);
      if (!bic) { setErr('bankNumber', 'BIC / SWIFT code is required'); bad = true; }
      else if (!isValidBic(bic)) { setErr('bankNumber', 'Enter a valid BIC / SWIFT code'); bad = true; }
    }

    const gold = parseGold();
    if (gold === null) { setErr('amount', 'Enter a valid Gold amount'); bad = true; }
    else if (gold < minGold) { setErr('amount', `Minimum is ${fmtGold(minGold)}`); bad = true; }
    else if (gold > maxGold) { setErr('amount', `Maximum is ${fmtGold(maxGold)}`); bad = true; }
    else if (gold > opts.goldBalance) { setErr('amount', `You only have ${fmtGold(opts.goldBalance)}`); bad = true; }
    if (bad || gold === null) return;

    const eurCents = goldToEurCents(gold);
    submitBtn.disabled = true;

    try {
      const res = await requestWithdrawal({
        method: method.id,
        amountCents: eurCents,
        firstName: first ?? undefined,
        lastName: last ?? undefined,
        ...(method.receiver === 'cardNumber'
          ? { cardNumber: normaliseCardNumber(inp('receiver').value) }
          : { iban: normaliseIban(inp('receiver').value), bankNumber: normaliseBic(inp('bankNumber').value) }),
      });
      if (destroyed) return;
      q('ok').textContent = res.message || 'Withdrawal requested.';
      q('ok').hidden = false;
      inp('receiver').value = '';
      inp('amount').value = '';
      renderPreview();
      opts.onSubmitted?.(res);
    } catch (err) {
      if (destroyed) return;
      submitBtn.disabled = false;
      const pe = toPaymentError(err);
      q('error').textContent = withdrawalErrorMessage(pe.code, pe.message, pe.details);
      q('error').hidden = false;
    }
  };

  el.addEventListener('click', onClick);
  inp('receiver').addEventListener('input', onReceiverInput);
  inp('bankNumber').addEventListener('input', () => {
    inp('bankNumber').value = formatBicInput(inp('bankNumber').value);
    setErr('bankNumber', null);
  });
  inp('amount').addEventListener('input', onAmountInput);
  form.addEventListener('submit', onSubmit);
  renderMethod();

  return {
    destroy() {
      destroyed = true;
      el.removeEventListener('click', onClick);
      inp('receiver').removeEventListener('input', onReceiverInput);
      inp('amount').removeEventListener('input', onAmountInput);
      form.removeEventListener('submit', onSubmit);
      el.innerHTML = '';
    },
  };
}
